/**
 * Data transformation utilities for FinProSidebar
 * Converts backend menuList format to sidebar groups format
 */

import type {
  BackendMenuItem,
  SidebarItem,
  SidebarItemGroup,
  TransformedSidebarData,
  UserInfo,
} from './sidebar-types'
import {
  GROUP_CONFIG,
  CODE_TO_ICON_MAP,
  CODE_TO_ITEM_ID_MAP,
  CODE_TO_ROUTE_MAP,
  CODE_TO_LABEL_OVERRIDE_MAP,
} from './sidebar-config'

/**
 * Convert a backend menu item to a SidebarItem with nested subitems preserved
 *
 * @param menuItem - Backend menu item that may contain subList
 * @returns Sidebar item with nested subItems if applicable
 */
function convertBackendItemToSidebarItem(menuItem: BackendMenuItem): SidebarItem {
  const code = menuItem.code
  const sidebarItem: SidebarItem = {
    id: CODE_TO_ITEM_ID_MAP[code] || code.toLowerCase(),
    label: CODE_TO_LABEL_OVERRIDE_MAP[code] || menuItem.name, // Use override label if available
    icon: CODE_TO_ICON_MAP[code] || CODE_TO_ICON_MAP['ACCOUNT-AGGREGATOR'], // Fallback icon
    route: CODE_TO_ROUTE_MAP[code] || menuItem.url || '/dashboard',
    permissionCode: code,
    visible: true,
  }

  // Recursively convert subList to subItems if it exists
  if (menuItem.subList && Array.isArray(menuItem.subList) && menuItem.subList.length > 0) {
    sidebarItem.subItems = menuItem.subList.map(subItem => convertBackendItemToSidebarItem(subItem))
  }

  return sidebarItem
}

/**
 * Flatten menuList to include items from subList arrays for route mapping
 * This ensures that ALL menu items (parent + nested) are available for route lookups
 *
 * @param menuList - Array of menu items that may contain nested subList items
 * @returns Flattened array of all menu items (top-level + nested)
 */
function flattenMenuListForRoutingOnly(menuList: BackendMenuItem[]): BackendMenuItem[] {
  const flattened: BackendMenuItem[] = []

  function flatten(items: BackendMenuItem[]) {
    items.forEach(item => {
      // Add the current item
      flattened.push(item)

      // Recursively flatten subList if it exists
      if (item.subList && Array.isArray(item.subList) && item.subList.length > 0) {
        flatten(item.subList)
      }
    })
  }

  flatten(menuList)
  return flattened
}

/**
 * Restructure menuList to move MIS-INSIGHTS to top-level
 *
 * This preprocessing step:
 * - Moves MIS-INSIGHTS from MIS subList to top-level (will be renamed to AA-Insights via label override)
 * - EWS is now fully config-driven from backend (no manual creation)
 *
 * Items will be grouped under INSIGHTS group in sidebar config.
 *
 * @param menuList - Original menuList from backend
 * @returns Restructured menuList with MIS-INSIGHTS as top-level item
 */
function restructureMISInsights(menuList: BackendMenuItem[]): BackendMenuItem[] {
  console.log('[Transform] restructureMISInsights called with menuList length:', menuList.length)

  // Deep clone to avoid mutating original
  const clonedList = JSON.parse(JSON.stringify(menuList)) as BackendMenuItem[]

  // Find MIS menu item
  const misItem = clonedList.find(item => item.code === 'MIS')
  console.log('[Transform] Found MIS item?', !!misItem, 'Has subList?', !!misItem?.subList, 'SubList length:', misItem?.subList?.length)

  if (!misItem || !misItem.subList || misItem.subList.length === 0) {
    // MIS not found or has no subList, return as is
    console.log('[Transform] MIS not found or has no subList, returning menuList as is')
    return clonedList
  }

  // Log all subList codes
  console.log('[Transform] MIS subList codes:', misItem.subList.map(sub => sub.code))

  // Find MIS-INSIGHTS in subList
  const misInsightsIndex = misItem.subList.findIndex(sub => sub.code === 'MIS-INSIGHTS')
  console.log('[Transform] MIS-INSIGHTS index in subList:', misInsightsIndex)

  if (misInsightsIndex === -1) {
    // MIS-INSIGHTS not found in subList, return as is
    console.log('[Transform] MIS-INSIGHTS not found in MIS subList, returning menuList as is')
    return clonedList
  }

  // Extract MIS-INSIGHTS item
  const misInsightsItem = misItem.subList[misInsightsIndex]
  console.log('[Transform] Extracted MIS-INSIGHTS item:', misInsightsItem)

  // Remove from MIS subList
  misItem.subList.splice(misInsightsIndex, 1)
  console.log('[Transform] Removed MIS-INSIGHTS from MIS subList. Remaining subList length:', misItem.subList.length)

  // Add MIS-INSIGHTS as flat top-level item (right after MIS for logical ordering)
  const misIndex = clonedList.findIndex(item => item.code === 'MIS')
  clonedList.splice(misIndex + 1, 0, misInsightsItem)
  console.log('[Transform] ✅ Added MIS-INSIGHTS as flat top-level item at index', misIndex + 1)
  console.log('[Transform] Top-level codes after restructure:', clonedList.map(item => item.code))

  return clonedList
}

/**
 * Transform backend menuList into sidebar groups structure
 *
 * This function takes the raw menu data from sessionStorage and converts it
 * into the format expected by FinProSidebar, grouping items by category,
 * assigning icons, and creating bidirectional route mappings.
 *
 * IMPORTANT: This function NOW PRESERVES the nested structure from subList arrays,
 * so menu items with submenus (like MIS, Admin, Central Registry) will have their
 * child items accessible via the subItems property.
 *
 * @param menuList - Array of menu items from sessionStorage.loggedUserInfo.menuList
 * @returns Transformed sidebar data with groups, items, and route mappings
 *
 * @example
 * const userInfo = JSON.parse(atob(sessionStorage.getItem('loggedUserInfo')))
 * const sidebarData = transformMenuListToGroups(userInfo.menuList)
 * // Use sidebarData.groups in FinProSidebar component
 */
export function transformMenuListToGroups(
  menuList: BackendMenuItem[]
): TransformedSidebarData {
  console.log('[Transform] Starting transformation with menuList length:', menuList.length)

  // PREPROCESSING: Restructure menuList to move MIS-INSIGHTS from MIS subList to top-level
  // This allows it to be part of the new INSIGHTS group instead of nested under MIS
  const restructuredMenuList = restructureMISInsights(menuList)

  console.log('[Transform] After restructuring, menuList length:', restructuredMenuList.length)

  // Create a map of backend menu items for quick lookup (preserving nested structure)
  // Also index nested items so GROUP_CONFIG can reference child codes directly
  const menuItemsMap = new Map<string, BackendMenuItem>()
  function indexMenuItems(items: BackendMenuItem[]) {
    items.forEach(item => {
      menuItemsMap.set(item.code, item)
      if (item.subList && Array.isArray(item.subList)) {
        indexMenuItems(item.subList)
      }
    })
  }
  indexMenuItems(restructuredMenuList)

  // Flatten only for checking available codes (for route mapping later)
  const flattenedForRouting = flattenMenuListForRoutingOnly(restructuredMenuList)
  const availableCodes = new Set(flattenedForRouting.map((item) => item.code))

  console.log('[Transform] Available codes:', Array.from(availableCodes))
  console.log('[Transform] Has MIS-INSIGHTS?', availableCodes.has('MIS-INSIGHTS'))
  console.log('[Transform] Has EWS?', availableCodes.has('EWS'))

  // Build groups by filtering items based on group configuration
  const groups: SidebarItemGroup[] = GROUP_CONFIG.map((groupConfig) => {
    console.log(`[Transform] Processing group: ${groupConfig.label}, codes:`, groupConfig.codes)

    // Filter menu items that belong to this group and are available to the user
    const items: SidebarItem[] = groupConfig.codes
      .filter((code) => {
        const hasCode = availableCodes.has(code)
        if (groupConfig.id === 'docs') {
          console.log(`[Transform]   - Code ${code}: available = ${hasCode}`)
        }
        return hasCode
      })
      .map((code) => {
        // Find the corresponding menu item from backend (using original list to preserve nesting)
        const menuItem = menuItemsMap.get(code)

        if (!menuItem) {
          console.warn(`[Transform] Menu item not found for code: ${code}`)
          return null
        }

        if (groupConfig.id === 'docs' && code === 'MIS-INSIGHTS') {
          console.log('[Transform]   - Found MIS-INSIGHTS menuItem:', menuItem)
        }

        // Convert to SidebarItem format (preserves nested subItems)
        return convertBackendItemToSidebarItem(menuItem)
      })
      .filter((item): item is SidebarItem => item !== null)

    if (groupConfig.id === 'docs') {
      console.log(`[Transform] REFERENCE & DOCS group has ${items.length} items:`, items.map(i => i.label))
      console.log(`[Transform] REFERENCE & DOCS detailed items:`, items.map(i => ({
        id: i.id,
        label: i.label,
        hasSubItems: !!i.subItems,
        subItemsCount: i.subItems?.length || 0,
        subItemsLabels: i.subItems?.map(sub => sub.label) || []
      })))
    }

    return {
      id: groupConfig.id,
      label: groupConfig.label,
      items,
      defaultOpen: groupConfig.defaultOpen,
      order: groupConfig.order,
    }
  }).filter((group) => group.items.length > 0) // Remove empty groups

  // Build route mappings for quick lookups during navigation (includes all items, even nested ones)
  const routeToItemMap: Record<string, string> = {}
  const itemToRouteMap: Record<string, string> = {}

  // Helper function to add routes from items and their subitems recursively
  function addRoutesRecursively(item: SidebarItem) {
    routeToItemMap[item.route] = item.id
    itemToRouteMap[item.id] = item.route

    // Add routes from subitems if they exist
    if (item.subItems && item.subItems.length > 0) {
      item.subItems.forEach(subItem => addRoutesRecursively(subItem))
    }
  }

  groups.forEach((group) => {
    group.items.forEach((item) => addRoutesRecursively(item))
  })

  console.log('[Transform] Sidebar data transformed:', {
    groupCount: groups.length,
    totalItems: groups.reduce((sum, g) => sum + g.items.length, 0),
    groups: groups.map(g => ({ id: g.id, label: g.label, itemCount: g.items.length, items: g.items.map(i => i.id) }))
  })

  return {
    groups,
    routeToItemMap,
    itemToRouteMap,
  }
}

/**
 * Map Angular route to sidebar item ID
 *
 * Used to determine which sidebar item should be highlighted when the
 * Angular route changes (e.g., browser back/forward, direct URL entry).
 *
 * @param route - Angular route (e.g., '/dashboard/template-list')
 * @param routeMap - Route to item mapping from transformed data
 * @returns Item ID or null if no mapping exists
 *
 * @example
 * const itemId = mapRouteToItemId('/dashboard/template-list', data.routeToItemMap)
 * // Returns: 'consent-templates'
 */
export function mapRouteToItemId(
  route: string,
  routeMap: Record<string, string>
): string | null {
  // Strip query parameters so routes like /dashboard/insights/ews?batch_id=... match /dashboard/insights/ews
  const routeWithoutParams = route.split('?')[0]
  return routeMap[routeWithoutParams] || routeMap[route] || null
}

/**
 * Map sidebar item ID to Angular route
 *
 * Used when a user clicks a sidebar item to determine which Angular
 * route to navigate to.
 *
 * @param itemId - Sidebar item ID (e.g., 'consent-templates')
 * @param itemMap - Item to route mapping from transformed data
 * @returns Angular route or null if no mapping exists
 *
 * @example
 * const route = mapItemIdToRoute('consent-templates', data.itemToRouteMap)
 * // Returns: '/dashboard/template-list'
 */
export function mapItemIdToRoute(
  itemId: string,
  itemMap: Record<string, string>
): string | null {
  return itemMap[itemId] || null
}

/**
 * Get icon component for a backend menu code
 *
 * Returns the appropriate Lucide icon for a given backend menu code.
 * Falls back to a default icon if the code is not recognized.
 *
 * @param code - Backend menu code (e.g., 'CONSENT-TEMPLATE')
 * @returns Lucide icon component
 */
export function getIconForCode(code: string) {
  return CODE_TO_ICON_MAP[code] || CODE_TO_ICON_MAP['ACCOUNT-AGGREGATOR']
}

/**
 * Load and parse user info from sessionStorage
 *
 * Safely retrieves and decodes the base64-encoded user information
 * from sessionStorage. Handles errors gracefully and provides fallback.
 *
 * @returns Parsed user info with menuList, or null if not available
 *
 * @example
 * const data = loadUserInfoFromSession()
 * if (data) {
 *   const sidebarData = transformMenuListToGroups(data.menuList)
 * }
 */
export function loadUserInfoFromSession(): {
  menuList: BackendMenuItem[]
  userInfo: UserInfo
} | null {
  try {
    const encoded = sessionStorage.getItem('loggedUserInfo')

    if (!encoded) {
      console.warn('[Transform] No loggedUserInfo in sessionStorage')
      return null
    }

    const decoded = JSON.parse(atob(encoded))

    if (!decoded || typeof decoded !== 'object') {
      console.error('[Transform] Invalid loggedUserInfo structure')
      return null
    }

    return {
      menuList: decoded.menuList || [],
      userInfo: decoded,
    }
  } catch (error) {
    console.error('[Transform] Failed to parse loggedUserInfo:', error)
    return null
  }
}
